/**
 * Pure utility functions — no React, no side effects.
 *
 * Keep this file lean. If a utility grows complex, extract it to its own file.
 *
 * @context CONTEXT:ENGINEERING:FRONTEND
 */
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Merge Tailwind CSS classes with conflict resolution.
 * Use this everywhere you conditionally apply Tailwind classes.
 *
 * @example
 * cn('px-4 py-2', isActive && 'bg-primary', className)
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Format a date to a human-readable string.
 * Always use this over raw Date methods to ensure consistent locale handling.
 */
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  })
}

/**
 * Format a number with locale-aware separators.
 */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return value.toLocaleString('en-US', options)
}

/**
 * Truncate a string to maxLength characters, appending an ellipsis if needed.
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str
  return str.slice(0, maxLength - 1) + '…'
}

/**
 * Create a debounced version of a function.
 * Use for search inputs, window resize handlers, etc.
 */
export function debounce<T extends (...args: unknown[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timer)
    timer = setTimeout(() => fn(...args), delay)
  }
}

/**
 * Sleep for a given number of milliseconds.
 * Useful in tests and dev tooling — do not use in production hot paths.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Exhaustive type check — ensures all union cases are handled in a switch.
 * @example
 * switch (status) {
 *   case 'active': return '...'
 *   case 'inactive': return '...'
 *   default: return assertNever(status)
 * }
 */
export function assertNever(value: never): never {
  throw new Error(`Unhandled case: ${String(value)}`)
}
